import {applyOperation} from '@github-ui/optimistic-connection'
import {connectionMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import type {LabelsQueryData} from './types'
import {buildRemoveLabelFromCurrentViewOperation, LABELS_CONNECTION_PATH} from './utils/label-connection-operations'
import {deleteLabelRequest} from './utils/label-requests'

type UseDeleteLabelOptions = {
  labelId: string
  isArchived: boolean
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setIsDialogOpen: (v: boolean) => void
  setErrorsPresent: (v: boolean) => void
}

type DeleteLabelHandler = () => void

const DELETE_MUTATION_META_KEY = 'repositoryLabelDelete'

function getQueryPathname(queryKey: QueryKey): string | undefined {
  const queryDeps = queryKey[4]
  if (!queryDeps || typeof queryDeps !== 'object' || Array.isArray(queryDeps) || !('pathname' in queryDeps)) return
  return typeof queryDeps.pathname === 'string' ? queryDeps.pathname : undefined
}

export function useDeleteLabel({
  labelId,
  isArchived,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions): DeleteLabelHandler {
  const queryClient = useQueryClient()
  const mutationKey = useMemo(() => connectionMutationKey(repositoryId, LABELS_CONNECTION_PATH), [repositoryId])

  const {mutate} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    meta: {[DELETE_MUTATION_META_KEY]: true},
    mutationFn: () => deleteLabelRequest(repositoryNameWithOwner, labelId),
    onError: () => {
      setErrorsPresent(true)
    },
    onSuccess: () => {
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base
            ? applyOperation(base, repositoryId, buildRemoveLabelFromCurrentViewOperation(labelId, isArchived))
            : base,
        )
      }
    },
    onSettled: async () => {
      const pendingDeleteCount = queryClient
        .getMutationCache()
        .findAll({mutationKey, status: 'pending'})
        .filter(mutation => mutation.options.meta?.[DELETE_MUTATION_META_KEY]).length
      if (!queryKey || pendingDeleteCount > 1) return

      const pathname = getQueryPathname(queryKey)
      const sourceQueryHash = hashKey(queryKey)
      await queryClient.invalidateQueries({
        queryKey: queryKey.slice(0, 4),
        predicate: query =>
          pathname !== undefined &&
          getQueryPathname(query.queryKey) === pathname &&
          hashKey(query.queryKey) !== sourceQueryHash,
        refetchType: 'all',
      })
    },
  })

  return useCallback(() => {
    setIsDialogOpen(false)
    mutate({operation: buildRemoveLabelFromCurrentViewOperation(labelId, isArchived)})
  }, [isArchived, labelId, mutate, setIsDialogOpen])
}
